import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
} from '@mui/material'
import {
  KingBed as KingBedIcon,
  RoomService as RoomServiceIcon,
  Wifi as WifiIcon,
  LocalParking as LocalParkingIcon,
  AcUnit as AcUnitIcon,
  Security as SecurityIcon,
  LocalLaundryService as LaundryIcon,
  CleaningServices as HousekeepingIcon,
} from '@mui/icons-material'

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
}

const features = [
  {
    icon: <KingBedIcon sx={{ fontSize: 40 }} />,
    title: 'Premium Rooms',
    description: 'Elegantly furnished rooms with modern amenities and city views'
  },
  {
    icon: <AcUnitIcon sx={{ fontSize: 40 }} />,
    title: 'Climate Control',
    description: 'Individual temperature control in each room for your comfort'
  },
  {
    icon: <WifiIcon sx={{ fontSize: 40 }} />,
    title: 'High-Speed WiFi',
    description: 'Stay connected with complimentary high-speed internet'
  },
  {
    icon: <RoomServiceIcon sx={{ fontSize: 40 }} />,
    title: '24/7 Room Service',
    description: 'Round-the-clock service at your convenience'
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: '24/7 Security',
    description: 'Advanced security systems and professional staff'
  },
  {
    icon: <LocalParkingIcon sx={{ fontSize: 40 }} />,
    title: 'Valet Parking',
    description: 'Convenient and secure parking for our guests'
  },
  {
    icon: <LaundryIcon sx={{ fontSize: 40 }} />,
    title: 'Laundry Service',
    description: 'Professional laundry and dry-cleaning services'
  },
  {
    icon: <HousekeepingIcon sx={{ fontSize: 40 }} />,
    title: 'Daily Housekeeping',
    description: 'Maintaining pristine cleanliness in your room'
  }
]

const FeaturesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  return (
    <Box
      component={motion.div}
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={staggerContainer}
      sx={{
        pt: { xs: 4, md: 6 },
        pb: { xs: 4, md: 6 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xl">
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{
            mb: { xs: 4, md: 5 },
            fontSize: { xs: '2rem', md: '2.75rem' },
          }}
        >
          Luxury Amenities
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={index}
              component={motion.div}
              variants={fadeInUp}
            >
              <Paper
                component={motion.div}
                whileHover={{ y: -10 }}
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  bgcolor: 'background.paper',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    '& .icon-container': {
                      transform: 'scale(1.1)',
                    }
                  },
                }}
              >
                <Box
                  className="icon-container"
                  sx={{
                    color: 'white',
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease',
                    '& svg': {
                      fontSize: 45,
                    }
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: 'primary.main'
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.6,
                    fontSize: '0.95rem'
                  }}
                >
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default FeaturesSection 